import { ComponentProps, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { MapRef } from 'react-map-gl/mapbox';
import { useDispatch, useSelector } from 'react-redux';
import useUser from '@alltrails/context/hooks/useUser';
import Link from '@alltrails/denali/components/Link';
import Info from '@alltrails/denali/icons/Info';
import Modal from '@alltrails/modal/components/Modal/Modal';
import type Context from '@alltrails/types/Context';
import useActivityChipsConfig from '../../hooks/filterConfigs/useActivityChipsConfig';
import useDifficultyChipsConfig from '../../hooks/filterConfigs/useDifficultyChipsConfig';
import useDistanceAwayFilterConfig from '../../hooks/filterConfigs/useDistanceAwayFilterConfig';
import useDurationFilterConfig from '../../hooks/filterConfigs/useDurationFilterConfig';
import useElevationGainFilterConfig from '../../hooks/filterConfigs/useElevationGainFilterConfig';
import useFeatureChipsConfig from '../../hooks/filterConfigs/useFeatureChipsConfig';
import useHighestPointFilterConfig from '../../hooks/filterConfigs/useHighestPointFilterConfig';
import useLengthFilterConfig from '../../hooks/filterConfigs/useLengthFilterConfig';
import useMinRatingFilterConfig from '../../hooks/filterConfigs/useMinRatingFilterConfig';
import useMyTrailsSegmentedConfig from '../../hooks/filterConfigs/useMyTrailsSegmentedConfig';
import useRouteTypeChipsConfig from '../../hooks/filterConfigs/useRouteTypeChipsConfig';
import useSuitabilityConfigV2 from '../../hooks/filterConfigs/useSuitabilityConfigV2';
import useVisitationChipsConfig from '../../hooks/filterConfigs/useVisitationChipsConfig';
import useDistanceAwayFilter from '../../hooks/useDistanceAwayFilter';
import useExploreServiceFilterCounts from '../../hooks/useExploreServiceFilterCounts';
import useExploreServiceFilterState from '../../hooks/useExploreServiceFilterState';
import { ExploreServiceState, updateCustomSortValue, updateNaturalLanguageQuery } from '../../redux/exploreServiceSlice';
import { buildRevampRangeSlider, withoutMarkLabels } from '../../utils/buildRevampRangeSlider';
import { defaultExploreServiceSort } from '../../utils/constants';
import styles from './AllFiltersModalV2.module.scss';
import FiltersModalV2 from './FiltersModalV2/FiltersModalV2';
import { formatRatingValue } from './formatRatingValue';

type AllFiltersModalV2Props = Pick<ComponentProps<typeof Modal>, 'onCloseRequest'> & {
  map?: MapRef;
  navigateTo: (step: 'difficulty' | 'wheelchair') => void;
  onSeeTrailsClick?: () => void;
};

const AllFiltersModalV2 = ({ map, navigateTo, onCloseRequest, onSeeTrailsClick }: AllFiltersModalV2Props) => {
  const intl = useIntl();
  const user = useUser();
  const dispatch = useDispatch();

  const { exploreServiceFilters, onFiltersChange, onSortChange, onTrailEngagementChanged, trailEngagement } = useExploreServiceFilterState();
  const { filterCount } = useExploreServiceFilterCounts();
  const { isLoading, customSortOptions, searchResultsSummary, useMetric } = useSelector(
    (state: { context: Context; exploreService: ExploreServiceState }) => ({
      isLoading: state.exploreService.searchIsLoading,
      customSortOptions: state.exploreService.customSortOptions,
      searchResultsSummary: state.exploreService.searchResultsSummary,
      useMetric: state.context.displayMetric
    })
  );

  const { applyDistanceAway, distanceAway, distanceAwaySliderSubHeaderSingleLine, localDistanceAway, setLocalDistanceAway } =
    useDistanceAwayFilter(map);
  const distanceAwayFilter = useDistanceAwayFilterConfig({
    appliedDistanceAway: distanceAway?.distance,
    distanceAway: localDistanceAway,
    onDistanceAwayChange: setLocalDistanceAway,
    onDistanceAwayChangeEnd: applyDistanceAway,
    subHeader: distanceAwaySliderSubHeaderSingleLine,
    useMetric
  });

  const lengthFilter = useLengthFilterConfig(exploreServiceFilters, onFiltersChange, useMetric);
  const elevationGainFilter = useElevationGainFilterConfig(exploreServiceFilters, onFiltersChange, useMetric, true /* useElevationGainLabel */);
  const highestPointFilter = useHighestPointFilterConfig(exploreServiceFilters, onFiltersChange, useMetric);
  const durationFilter = useDurationFilterConfig(exploreServiceFilters, onFiltersChange);
  const minRatingFilter = useMinRatingFilterConfig(exploreServiceFilters, onFiltersChange, rating =>
    formatRatingValue(rating, { showStarForAny: true })
  );

  const difficultyChips = useDifficultyChipsConfig(exploreServiceFilters, onFiltersChange);
  const activityChips = useActivityChipsConfig(exploreServiceFilters, onFiltersChange);
  const featureChips = useFeatureChipsConfig(exploreServiceFilters, onFiltersChange);
  const routeTypeChips = useRouteTypeChipsConfig(exploreServiceFilters, onFiltersChange);
  const visitationChips = useVisitationChipsConfig(exploreServiceFilters, onFiltersChange);
  const { accessibilityChips, dogToggle, kidToggle } = useSuitabilityConfigV2(exploreServiceFilters, onFiltersChange);
  const { completedConfig, savedConfig } = useMyTrailsSegmentedConfig(trailEngagement, onTrailEngagementChanged);

  const difficultyLink = useMemo(
    () => (
      <Link className={styles.aboutLink} noUnderline onClick={() => navigateTo('difficulty')} size="sm" variant="secondary">
        <span>
          <FormattedMessage defaultMessage="About difficulty" />
        </span>
        <Info color="--color-text-secondary" size="xs" />
      </Link>
    ),
    [navigateTo]
  );

  const wheelchairLink = useMemo(
    () => (
      <Link className={styles.aboutLink} noUnderline onClick={() => navigateTo('wheelchair')} size="sm" variant="secondary">
        <span>
          <FormattedMessage defaultMessage="About wheelchair-friendliness" />
        </span>
        <Info color="--color-text-secondary" size="xs" />
      </Link>
    ),
    [navigateTo]
  );

  const sections: ComponentProps<typeof FiltersModalV2>['sections'] = useMemo(
    () => [
      {
        id: 'difficulty',
        title: difficultyChips.selectionChips.title,
        titleSubtext: difficultyLink,
        configs: [difficultyChips]
      },
      { id: 'length', configs: [{ type: 'range-slider', id: 'length', rangeSlider: buildRevampRangeSlider(lengthFilter.rangeSlider, intl) }] },
      {
        id: 'elevation-gain',
        configs: [{ type: 'range-slider', id: 'elevationGain', rangeSlider: buildRevampRangeSlider(elevationGainFilter.rangeSlider, intl) }]
      },
      {
        id: 'highest-point',
        configs: [{ type: 'range-slider', id: 'highestPoint', rangeSlider: buildRevampRangeSlider(highestPointFilter.rangeSlider, intl) }]
      },
      {
        id: 'estimated-time',
        configs: [
          { type: 'range-slider', id: 'duration', rangeSlider: buildRevampRangeSlider(durationFilter.rangeSlider, intl, { useAnyAsDefault: true }) }
        ]
      },
      {
        id: 'distance-away',
        configs: [
          {
            type: 'slider',
            id: 'distanceAway',
            slider: { ...distanceAwayFilter.slider, compactSubHeader: true, marks: withoutMarkLabels(distanceAwayFilter.slider.marks) }
          }
        ]
      },
      { id: 'suitability-toggles', configs: [dogToggle, kidToggle] },
      { id: 'activity', title: activityChips.selectionChips.title, configs: [activityChips] },
      { id: 'features', title: featureChips.selectionChips.title, configs: [featureChips] },
      { id: 'route-type', title: routeTypeChips.selectionChips.title, configs: [routeTypeChips] },
      {
        id: 'accessibility',
        title: accessibilityChips.selectionChips.title,
        titleSubtext: wheelchairLink,
        configs: [accessibilityChips]
      },
      { id: 'visitation', title: visitationChips.selectionChips.title, configs: [visitationChips] },
      {
        id: 'community-rating',
        configs: [
          {
            type: 'slider',
            id: 'minRating',
            slider: {
              ...minRatingFilter.slider,
              label: <FormattedMessage defaultMessage="Community rating" />,
              marks: withoutMarkLabels(minRatingFilter.slider.marks)
            }
          }
        ]
      },
      ...(user ? [{ id: 'my-trails', title: <FormattedMessage defaultMessage="My trails" />, configs: [savedConfig, completedConfig] }] : [])
    ],
    [
      accessibilityChips,
      activityChips,
      completedConfig,
      difficultyChips,
      difficultyLink,
      distanceAwayFilter.slider,
      dogToggle,
      durationFilter.rangeSlider,
      elevationGainFilter.rangeSlider,
      featureChips,
      highestPointFilter.rangeSlider,
      intl,
      kidToggle,
      lengthFilter.rangeSlider,
      minRatingFilter.slider,
      routeTypeChips,
      savedConfig,
      user,
      visitationChips,
      wheelchairLink
    ]
  );

  const onClear = filterCount
    ? () => {
        applyDistanceAway(undefined);
        onFiltersChange({});
        onSortChange(defaultExploreServiceSort);
        if (customSortOptions) {
          dispatch(updateCustomSortValue(customSortOptions[0].value));
        }
        onTrailEngagementChanged([]);
        dispatch(updateNaturalLanguageQuery(undefined));
      }
    : undefined;

  return (
    <FiltersModalV2
      onCloseRequest={onCloseRequest}
      onSeeTrailsClick={onSeeTrailsClick}
      sections={sections}
      onClear={onClear}
      trailResultCount={isLoading ? null : (searchResultsSummary?.count ?? 0)}
    />
  );
};

export default AllFiltersModalV2;
